import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Trash2, Loader2, LogOut, Download, Mail, Search, Users } from "lucide-react";
import AdminTabs from "@/components/AdminTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "artlenss-admin-token";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const AdminNewsletter = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [query, setQuery] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [pollingId, setPollingId] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/admin/newsletter/subscribers`, { headers: authHeaders });
      setSubscribers(data.subscribers || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("انتهت الجلسة، أعد تسجيل الدخول");
      }
    }
  };

  // Initial load + real-time polling every 5s (so new subscriptions from the footer appear instantly)
  useEffect(() => {
    if (!token) return;
    load();
    const id = setInterval(load, 5000);
    setPollingId(id);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doLogin = async () => {
    if (!password.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
      toast.success("مرحباً بك 📬");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "كلمة المرور غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => {
    if (pollingId) clearInterval(pollingId);
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSubscribers([]);
    toast("تم تسجيل الخروج");
  };

  const deleteSub = async (sub) => {
    if (!window.confirm(`حذف المشترك "${sub.email}" نهائياً؟`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${sub.id}`, { headers: authHeaders });
      setSubscribers((list) => list.filter((s) => s.id !== sub.id));
      toast.success("تم الحذف");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الحذف");
    }
  };

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/newsletter/export.csv`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const filename = `artlenss-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تحميل القائمة");
    } catch (err) {
      toast.error("فشل التحميل");
    } finally {
      setDownloading(false);
    }
  };

  // -------- LOGIN VIEW --------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center p-4" data-testid="admin-newsletter-login">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0d0d10] p-8 md:p-10">
          <div className="w-14 h-14 rounded-full bg-[#E6A95B] text-[#0A0A0C] grid place-items-center mx-auto mb-6">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-display text-3xl text-center mb-2">لوحة المشتركين</h1>
          <p className="text-white/50 text-center text-sm mb-8">ARTLENSS Newsletter Admin</p>
          <label className="block text-xs uppercase tracking-widest text-[#E6A95B] mb-2">كلمة المرور</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doLogin()}
            data-testid="admin-newsletter-password"
            className="w-full bg-black/40 border border-white/15 rounded-lg px-4 py-3 focus:outline-none focus:border-[#E6A95B]"
            autoFocus
          />
          <button
            onClick={doLogin}
            disabled={loading}
            data-testid="admin-newsletter-login-btn"
            className="mt-6 w-full py-3 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            دخول
          </button>
        </div>
      </div>
    );
  }

  // Filter by query
  const filtered = query.trim()
    ? subscribers.filter((s) => (s.email || "").toLowerCase().includes(query.trim().toLowerCase()))
    : subscribers;

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="admin-newsletter-dashboard">
      <header className="border-b border-white/10 px-6 py-5">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="text-[#E6A95B] text-xs uppercase tracking-widest">ARTLENSS · Admin</div>
            <div className="font-display text-2xl">المشتركين في النشرة البريدية</div>
          </div>
          <button onClick={doLogout} className="text-white/60 hover:text-white p-2 inline-flex items-center gap-2 text-sm" title="خروج">
            <LogOut className="w-4 h-4" /> خروج
          </button>
        </div>
        <AdminTabs />
      </header>

      <main className="p-6 md:p-10 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E6A95B]/15 text-[#E6A95B] grid place-items-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-white/50 text-xs uppercase tracking-widest">إجمالي المشتركين</div>
                <div className="font-display text-3xl" data-testid="subscriber-count">{subscribers.length}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E6A95B]/15 text-[#E6A95B] grid place-items-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 2l2.4 7.4H22l-6.2 4.5L18.2 22 12 17.5 5.8 22l2.4-8.1L2 9.4h7.6z"/></svg>
              </div>
              <div>
                <div className="text-white/50 text-xs uppercase tracking-widest">طالبو استشارة</div>
                <div className="font-display text-3xl text-[#E6A95B]" data-testid="consultation-count">
                  {subscribers.filter((s) => s.interest === "consultation" || s.source === "consultation").length}
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={downloadCsv}
            disabled={downloading || subscribers.length === 0}
            data-testid="download-csv-btn"
            className="px-6 py-3 rounded-full bg-[#E6A95B] text-[#0A0A0C] font-bold text-sm inline-flex items-center gap-2 hover:brightness-110 hover:shadow-[0_0_30px_-5px_rgba(230,169,91,0.6)] disabled:opacity-50 transition"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            تحميل القائمة (CSV)
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="w-4 h-4 text-white/40 absolute top-1/2 -translate-y-1/2 start-4 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ابحث عن بريد إلكتروني..."
            data-testid="subscriber-search"
            className="w-full h-11 rounded-full bg-black/40 border border-white/15 ps-11 pe-4 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#E6A95B]"
          />
        </div>

        {subscribers.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-2xl p-16 text-center text-white/60" data-testid="no-subscribers">
            <Mail className="w-10 h-10 mx-auto mb-4 text-white/30" />
            <p>لا يوجد مشتركون بعد.</p>
            <p className="text-xs mt-2 text-white/40">سيظهر أي بريد يُدخل عبر الفوتر هنا فوراً.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-2xl p-10 text-center text-white/60">
            لا توجد نتائج مطابقة لبحثك.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#0d0d10] overflow-hidden" data-testid="subscribers-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-[#E6A95B] text-xs uppercase tracking-widest">
                  <th className="text-start p-4 font-normal">البريد الإلكتروني</th>
                  <th className="text-start p-4 font-normal">النوع</th>
                  <th className="text-start p-4 font-normal hidden sm:table-cell">اللغة</th>
                  <th className="text-start p-4 font-normal hidden md:table-cell">المصدر</th>
                  <th className="text-start p-4 font-normal">تاريخ الاشتراك</th>
                  <th className="p-4 font-normal text-center">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const isConsultation = s.interest === "consultation" || s.source === "consultation";
                  return (
                  <tr
                    key={s.id}
                    data-testid={`subscriber-row-${s.id}`}
                    className={`border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition ${isConsultation ? 'bg-[#E6A95B]/[0.06]' : ''}`}
                  >
                    <td className="p-4 font-mono text-sm text-white" dir="ltr">{s.email}</td>
                    <td className="p-4">
                      {isConsultation ? (
                        <span
                          data-testid="consultation-badge"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E6A95B]/20 border border-[#E6A95B]/50 text-[#E6A95B] text-[11px] font-bold uppercase tracking-wider"
                        >
                          <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor"><path d="M12 2l2.4 7.4H22l-6.2 4.5L18.2 22 12 17.5 5.8 22l2.4-8.1L2 9.4h7.6z"/></svg>
                          طالب استشارة
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-white/60 text-[11px] uppercase tracking-wider">
                          نشرة عامة
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-white/60 text-sm hidden sm:table-cell">{s.lang === "ar" ? "العربية" : "English"}</td>
                    <td className="p-4 text-white/50 text-xs hidden md:table-cell">{s.source || "—"}</td>
                    <td className="p-4 text-white/60 text-xs">{formatDate(s.subscribed_at)}</td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => deleteSub(s)}
                        data-testid={`delete-subscriber-${s.id}`}
                        className="p-2 text-white/50 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-white/40 text-xs text-center mt-8">
          يتم تحديث القائمة تلقائياً كل 5 ثوانٍ · أي بريد يُضاف عبر الفوتر يظهر هنا فوراً
        </p>
      </main>
    </div>
  );
};

export default AdminNewsletter;
